import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { PropsWithChildren } from 'react';

import { overlayBunja } from '@flipster/overlay';
import type { TradeMode } from '@flipster/r230522/model/prex.v2.r230522.tables.private_account';
import { SubAccountIdContext, SubAccountIdScope } from '@flipster/r230522/normal-sub/sub-account';
import { Providers } from '@flipster/react/providers/providers';
import { OrderMarginType } from '@flipster/utils/types/base';

import { TpslModal } from '.';
import { SymbolContext, SymbolScope } from '../shared/symbol/bunja';
import type { PositionType } from '../shared/tpsl/bunja';
import { ParentIdContext, ParentIdScope, PositionTypeContext, PositionTypeScope } from '../shared/tpsl/bunja';
import { TradeModeContext, TradeModeScope } from '../trade-mode/state';
import { ModalErrorBoundary } from '../utils/modal-error-boundary';
import {
  CanLimitTpslContext,
  CanLimitTpslScope,
  InitialTriggerPriceContext,
  ModifyOrderIdContext,
  PositionSlotContext,
  PositionSlotScope,
  TpslType,
  TpslTypeContext,
  TpslTypeScope,
} from './state';
import { MarginTypeContext, MarginTypeScope } from './state/base';

export function useTpslModalOverlay(
  symbol: string,
  positionType: PositionType,
  tpslType: TpslType,
  slot: number,
  marginType: OrderMarginType | undefined,
  parentId: string,
  accountId?: number,
  tradeMode?: TradeMode,
  canLimitTpsl: boolean = true,
) {
  return useBunja(tpslModalOverlayBunja, [
    SymbolScope.bind(symbol),
    PositionTypeScope.bind(positionType),
    PositionSlotScope.bind(slot),
    TpslTypeScope.bind(tpslType),
    MarginTypeScope.bind(marginType),
    ParentIdScope.bind(parentId),
    SubAccountIdScope.bind(accountId),
    TradeModeScope.bind(tradeMode),
    CanLimitTpslScope.bind(canLimitTpsl),
  ]);
}

export const tpslModalOverlayBunja = bunja(() => {
  const overlay = bunja.use(overlayBunja);

  const symbol = bunja.use(SymbolScope);
  const positionType = bunja.use(PositionTypeScope);
  const slot = bunja.use(PositionSlotScope);
  const tpslType = bunja.use(TpslTypeScope);
  const marginType = bunja.use(MarginTypeScope);
  const parentId = bunja.use(ParentIdScope);
  const accountId = bunja.use(SubAccountIdScope);
  const tradeMode = bunja.use(TradeModeScope);
  const canLimitTpsl = bunja.use(CanLimitTpslScope);

  /**
   * @param opts 호출 시점 override.
   *   - tpslType: 차트 드래그 방향(TP/SL)처럼 열 때마다 달라지는 경우 바인딩된 scope를 덮어쓴다.
   *   - initialTriggerPrice: 차트 드래그/드롭 가격으로 trigger price 입력을 prefill (생성/수정 모두).
   *   - modifyOrderId: 기존 order line을 끌어 "수정"으로 열 때 그 주문 id(entire→entire-update /
   *     partial→partial-update). 없으면 생성 모드.
   */
  function openTpslModal(opts?: { tpslType?: TpslType; initialTriggerPrice?: number; modifyOrderId?: string }) {
    return overlay.open(({ isOpen, close, unmount }) => (
      <TpslModalContextProvider
        symbol={symbol}
        positionType={positionType}
        tpslType={opts?.tpslType ?? tpslType}
        slot={slot}
        marginType={marginType}
        parentId={parentId}
        accountId={accountId}
        tradeMode={tradeMode}
        canLimitTpsl={canLimitTpsl}
        initialTriggerPrice={opts?.initialTriggerPrice}
        modifyOrderId={opts?.modifyOrderId}
      >
        <ModalErrorBoundary onError={close}>
          <TpslModal isOpen={isOpen} externalOnClose={close} onExit={unmount} />
        </ModalErrorBoundary>
      </TpslModalContextProvider>
    ));
  }

  return {
    openTpslModal,
  };
});
tpslModalOverlayBunja.debugLabel = 'tpslModalOverlayBunja';

export interface TpslModalContextProviderProps extends PropsWithChildren {
  symbol: string;
  positionType: PositionType;
  slot: number;
  tpslType: TpslType;
  marginType: OrderMarginType | undefined;
  parentId: string;
  accountId?: number;
  tradeMode?: TradeMode;
  canLimitTpsl?: boolean;
  initialTriggerPrice?: number;
  modifyOrderId?: string;
}
export const TpslModalContextProvider = ({
  positionType,
  tpslType,
  symbol,
  slot,
  marginType,
  parentId,
  accountId,
  tradeMode,
  canLimitTpsl = true,
  initialTriggerPrice,
  modifyOrderId,
  children,
}: TpslModalContextProviderProps) => {
  return (
    <Providers
      providers={
        <>
          <SymbolContext value={symbol} />
          <PositionTypeContext value={positionType} />
          <PositionSlotContext value={slot} />
          <TpslTypeContext value={tpslType} />
          <MarginTypeContext value={marginType} />
          <ParentIdContext value={parentId} />
          <SubAccountIdContext value={accountId} />
          <TradeModeContext value={tradeMode} />
          <CanLimitTpslContext value={canLimitTpsl} />
          <InitialTriggerPriceContext value={initialTriggerPrice} />
          <ModifyOrderIdContext value={modifyOrderId} />
        </>
      }
    >
      {children}
    </Providers>
  );
};
